<template>
  <div class="ola-submenu" :class="{
    'ola-submenu-active': active,
    'ola-submenu-opened': opened
  }" @mouseenter="handleMouseenter" @mouseleave="handleMouseleave">
    <a v-if="href" :href="href" :target="target" class="ola-submenu-title" @click.stop.prevent="handleDirectClick"
      @touchstart.stop.prevent="handleTouchStart" @touchend.stop.prevent="handleTouchEnd">
      <slot name="icon"></slot>
      <span class="ola-submenu-title-content" :class="{ 'ola-submenu-title-content-active': active }">
        <slot name="title"></slot>
      </span>
      <i class="ola-submenu-arrow" :class="{ 'ola-submenu-arrow-opened': opened }"></i>
    </a>
    <div v-else class="ola-submenu-title" @click.stop="handleDirectClick" @touchstart.stop.prevent="handleTouchStart"
      @touchend.stop.prevent="handleTouchEnd">
      <slot name="icon"></slot>
      <span class="ola-submenu-title-content" :class="{ 'ola-submenu-title-content-active': active }">
        <slot name="title"></slot>
      </span>
      <i class="ola-submenu-arrow" :class="{ 'ola-submenu-arrow-opened': opened }"></i>
    </div>
    <transition name="ola-zoom-in-top">
      <div v-show="opened" class="ola-submenu-content">
        <div class="ola-submenu-inner" @mouseenter="onSubMenuEnter" @mouseleave="onSubMenuLeave"
          @touchstart.stop="onSubMenuEnter" @touchend.stop="onSubMenuLeave">
          <slot></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'OlaSubMenu',
  props: {
    // 子菜单索引
    index: {
      type: String,
      required: true
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 链接地址，用于SEO友好的a标签
    href: {
      type: String,
      default: ''
    },
    // 链接打开方式
    target: {
      type: String,
      default: '_self'
    },
    // 路径匹配模式，用于确定当前菜单项是否激活
    pathPattern: {
      type: [String, RegExp],
      default: null
    }
  },
  inject: ['rootMenu'],
  data() {
    return {
      timeout: null,
      mouseInMenu: false,
      mouseInSubMenu: false,
      touchTimeout: null,
      isTouching: false,
      hasTouchStart: false,
      touchPosition: { x: 0, y: 0 },
      isRouteActive: false
    };
  },
  computed: {
    // 当前子菜单是否处于打开状态
    opened() {
      return this.rootMenu.openedMenus.includes(this.index);
    },
    // 当前子菜单是否处于激活状态
    active() {
      // 如果路由匹配成功，则激活
      if (this.isRouteActive) return true;

      const activeIndex = this.rootMenu.activeIndex;
      // 如果当前激活的菜单项属于此子菜单，则该子菜单处于激活状态
      return Object.keys(this.rootMenu.items).some(key => {
        return key.startsWith(this.index + '-') && this.rootMenu.items[key].active;
      });
    },
    // 是否为水平模式
    isHorizontal() {
      return this.rootMenu.mode === 'horizontal';
    },
    // 是否为折叠模式
    isCollapse() {
      return this.rootMenu.collapse;
    },
    // 是否为触摸设备
    isTouchDevice() {
      return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    },
    // 是否为PC端
    isPC() {
      return !this.isTouchDevice;
    }
  },
  methods: {
    // 直接处理点击事件，不再通过中间方法
    handleDirectClick(event) {
      if (this.disabled) return;
      event.stopPropagation();

      // 无论设备类型，统一处理逻辑
      if (this.opened) {
        this.rootMenu.closeMenu(this.index);
      } else {
        // 关闭其他打开的子菜单
        this.rootMenu.openedMenus.forEach(index => {
          if (index !== this.index) {
            this.rootMenu.closeMenu(index);
          }
        });
        this.rootMenu.openMenu(this.index);

        // 更新子菜单位置
        this.$nextTick(() => {
          setTimeout(() => {
            this.updateSubmenuPosition();
          }, 50);
        });
      }
    },

    // 检查当前路由是否匹配
    checkRouteMatch() {
      if (!this.$route) return false;

      const currentPath = this.$route.path;

      // 如果有明确的路径匹配模式
      if (this.pathPattern) {
        if (this.pathPattern instanceof RegExp) {
          return this.pathPattern.test(currentPath);
        }
        return currentPath.startsWith(this.pathPattern);
      }

      // 使用href属性进行匹配
      if (this.href && this.href !== '/') {
        // 对于首页，只有当路径完全等于/时才匹配
        if (this.href === '/') {
          return currentPath === '/';
        }
        // 对于其他页面，只要路径以href开头就匹配
        return currentPath.startsWith(this.href);
      }

      return false;
    },

    // 更新路由匹配状态
    updateRouteActiveState() {
      this.isRouteActive = this.checkRouteMatch();
    },

    // 保留旧方法以兼容其他地方的调用
    handleTitleClick(event) {
      this.handleDirectClick(event);
    },

    // 旧的点击处理方法保留但不再使用
    handleClick(event) {
      this.handleDirectClick(event);
    },

    handleMouseenter() {
      if (this.disabled || this.isTouching || this.isTouchDevice) return;

      clearTimeout(this.timeout);

      if (this.isHorizontal || (this.isCollapse && !this.isHorizontal)) {
        this.timeout = setTimeout(() => {
          this.rootMenu.openMenu(this.index);
        }, 200);
      }

      this.mouseInMenu = true;
    },

    handleMouseleave() {
      if (this.disabled || this.isTouching || this.isTouchDevice) return;

      if (this.isHorizontal || (this.isCollapse && !this.isHorizontal)) {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          // 只有当鼠标离开菜单和子菜单后才关闭
          if (!this.mouseInMenu && !this.mouseInSubMenu) {
            this.rootMenu.closeMenu(this.index);
          }
        }, 300);
      }

      this.mouseInMenu = false;
    },

    // 触摸事件处理
    handleTouchStart(event) {
      if (this.disabled) return;

      this.hasTouchStart = true;
      this.isTouching = true;

      // 记录触摸起始位置
      if (event.touches && event.touches[0]) {
        this.touchPosition.x = event.touches[0].clientX;
        this.touchPosition.y = event.touches[0].clientY;
      }

      clearTimeout(this.touchTimeout);
    },

    // 触摸结束事件
    handleTouchEnd(event) {
      if (this.disabled) return;

      // 检查是否为点击而非滑动
      let isClick = true;
      if (event.changedTouches && event.changedTouches[0]) {
        const diffX = Math.abs(event.changedTouches[0].clientX - this.touchPosition.x);
        const diffY = Math.abs(event.changedTouches[0].clientY - this.touchPosition.y);

        // 如果移动距离较大，认为是滑动而非点击
        if (diffX > 10 || diffY > 10) {
          isClick = false;
        }
      }

      // 如果是点击操作，则直接调用handleDirectClick处理
      if (isClick && this.hasTouchStart) {
        this.handleDirectClick(event);
      }

      // 延迟重置触摸状态
      this.touchTimeout = setTimeout(() => {
        this.isTouching = false;
        this.hasTouchStart = false;
      }, 300);
    },

    // 子菜单鼠标进入
    onSubMenuEnter() {
      this.mouseInSubMenu = true;
      clearTimeout(this.timeout);
    },

    // 子菜单鼠标离开
    onSubMenuLeave() {
      this.mouseInSubMenu = false;

      if (this.isHorizontal || (this.isCollapse && !this.isHorizontal)) {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          // 只有当鼠标离开菜单和子菜单后才关闭
          if (!this.mouseInMenu && !this.mouseInSubMenu) {
            this.rootMenu.closeMenu(this.index);
          }
        }, 300);
      }
    },

    // 检测点击其他区域关闭子菜单
    handleDocumentClick(event) {
      // 检查点击是否在当前子菜单外部
      const target = event.target;
      const isOutside = !this.$el.contains(target);

      if (isOutside && this.opened) {
        this.rootMenu.closeMenu(this.index);
      }
    },

    // 更新子菜单位置
    updateSubmenuPosition() {
      // 确保在客户端环境中执行
      if (typeof window === 'undefined') return;

      // 获取子菜单元素
      const submenuEl = this.$el.querySelector('.ola-submenu-content');
      if (!submenuEl) return;

      // 获取视口宽度
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      // 获取子菜单的位置和尺寸
      const rect = submenuEl.getBoundingClientRect();

      // 如果子菜单超出视口右侧，则向左显示
      if (rect.right > viewportWidth) {
        submenuEl.style.left = 'auto';
        submenuEl.style.right = '0';
      } else {
        submenuEl.style.left = '';
        submenuEl.style.right = '';
      }
    }
  },
  mounted() {
    this.rootMenu.addSubmenu(this);

    // 初始检查路由匹配
    this.updateRouteActiveState();

    // 监听路由变化
    if (this.$router) {
      this.$watch('$route', () => {
        this.updateRouteActiveState();
      });
    }

    // 如果当前子菜单在默认打开列表中，则打开它
    if (this.rootMenu.defaultOpeneds.includes(this.index)) {
      this.rootMenu.openMenu(this.index);
    }

    // 检测点击其他区域关闭子菜单
    if (typeof document !== 'undefined') {
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('touchend', this.handleDocumentClick);
    }

    // 初始化子菜单位置
    this.$nextTick(() => {
      this.updateSubmenuPosition();
    });

    // 在iPad等触摸设备上重新定位子菜单
    if (this.isTouchDevice && this.isHorizontal) {
      // 监听窗口大小变化，更新子菜单位置
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', this.updateSubmenuPosition);
      }
    }
  },
  beforeUnmount() {
    this.rootMenu.removeSubmenu(this);

    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('touchend', this.handleDocumentClick);
    }

    if (this.isTouchDevice && this.isHorizontal && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateSubmenuPosition);
    }
  }
};
</script>

<style>
.ola-submenu {
  position: relative;
  list-style: none;
}

.ola-submenu-title {
  height: 56px;
  line-height: 56px;
  padding: 0 16px;
  font-size: 16px;
  color: #303133;
  cursor: pointer;
  position: relative;
  transition: all .3s;
  display: flex;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
  /* 完全移除iOS点击高亮 */
}

/* .ola-submenu-title:hover {
  background-color: #f5f7fa;
} */

.ola-submenu-title-content {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ola-submenu-title-content-active {
  font-weight: 500;
}

.ola-submenu-arrow {
  position: relative;
  margin-left: 14px;
  top: -3px;
  transition: transform .3s;
  font-size: 12px;
  transform-origin: center center;
}

.ola-submenu-arrow:after {
  content: '';
  position: absolute;
  width: 6px;
  height: 6px;
  border-right: 2px solid #909399;
  border-top: 2px solid #909399;
  transform: rotate(135deg);
  transform-origin: center center;
  top: -3px;
  left: -5px;
}

.ola-submenu-arrow-opened {
  transform: rotate(180deg);
  top: 2px;

  &::after {
    border-right: 2px solid var(--header-main-color, #ff1f59);
    border-top: 2px solid var(--header-main-color, #ff1f59);
  }
}

.ola-submenu-content {
  list-style: none;
  padding: 0;
  margin: 0;
  /* background-color: #fff; */
  overflow: hidden;
}

.ola-menu-horizontal .ola-submenu {
  float: left;
}

.ola-menu-horizontal .ola-submenu-title {
  height: 63px;
  line-height: 63px;
}

.ola-menu-horizontal .ola-submenu-content {
  position: absolute;
  top: 52px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  /* 提高z-index确保子菜单显示在其他元素上方 */
  min-width: 112px;
  text-align: center;
  padding: 8px;
}
.ola-menu-horizontal .ola-submenu-inner {
  margin-top: 10px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .1);
  border-radius: 8px;
   min-width: 112px;
  padding: 10px 0;
}
.ola-menu-horizontal .ola-submenu-active>.ola-submenu-title {
  color: #333;
  font-weight: 500;
}

/* 垂直模式下的子菜单样式 */
.ola-menu-vertical .ola-submenu-content {
  overflow: hidden;
  padding-left: 20px;
  border-left: 1px solid #e6e6e6;
  margin-left: 20px;
}

/* 折叠模式下的子菜单样式 */
.ola-menu-collapse .ola-submenu-title {
  padding: 0;
  height: 56px;
  line-height: 56px;
  text-align: center;
}

.ola-menu-collapse .ola-submenu-title-content {
  display: none;
}

.ola-menu-collapse .ola-submenu-arrow {
  display: none;
}

.ola-menu-collapse .ola-submenu-content {
  position: absolute;
  left: 64px;
  top: 0;
  border: 1px solid #e6e6e6;
  /* background-color: #fff; */
  z-index: 100;
  min-width: 200px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .1);
  border-radius: 4px;
  padding: 5px 0;
}

/* 嵌套子菜单样式 */
.ola-submenu .ola-submenu .ola-submenu-title {
  padding-left: 40px;
}

.ola-menu-horizontal .ola-submenu .ola-submenu .ola-submenu-title {
  padding-left: 20px;
}

.ola-menu-horizontal .ola-submenu .ola-submenu .ola-submenu-content {
  left: 100%;
  top: 0;
}

/* 动画效果 */
.ola-zoom-in-top-enter-active,
.ola-zoom-in-top-leave-active {
  opacity: 1;
  transition: opacity .3s cubic-bezier(.23, 1, .32, 1);
}

.ola-zoom-in-top-enter-from,
.ola-zoom-in-top-leave-to {
  opacity: 0;
}

@media screen and (max-width: 763px) {
  .ola-menu-horizontal .ola-submenu {
    width: 100%;
    float: none;
  }

  .ola-menu-horizontal .ola-submenu-title {
    height: 50px;
    line-height: 50px;
  }

  .ola-menu-horizontal .ola-submenu-content {
    position: relative;
    top: 0;
    left: 0;
    border: none;
    box-shadow: none;
    width: 100%;
  }
}
@media (max-width: 1024px) {
  .ola-submenu .ola-submenu-title{
    padding-left: 0;
  }
}
/* 触摸设备特殊样式 */
@media (pointer: coarse) {
  .ola-submenu-title {
    padding: 0 15px;
  }
  .ola-submenu .ola-submenu-title{
    padding-left: 0;
  }
  .ola-menu-horizontal .ola-submenu-title {
    min-height: 63px;
  }

  /* iPad特定修复 - 确保子菜单显示在正确位置 */
  .ola-menu-horizontal .ola-submenu-content {
    max-width: calc(100vw - 20px);
    box-sizing: border-box;
  }
}

/* iPad特定样式 */
@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
  .ola-submenu-title {
    position: relative;
  }

  .ola-menu-horizontal .ola-submenu-content {
    min-width: 150px;
  }

  /* 确保子菜单不会超出屏幕 */
  .ola-menu-horizontal .ola-submenu:last-child .ola-submenu-content {
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>